<template>
  <div class="flex flex-col gap-16 px-6 py-8">
    <TabPanels>
      <TabPanel>
        <section>
          <h2 class="text-headline-medium mb-4">{{ $t('lumia.sections.about') }}</h2>
          <LessonImpulseField class="text-body-large" :value="impulse.description" />
          <section class="bg-primary-container rounded-large mt-10 p-6">
            <div class="text-on-primary-container mb-2 flex flex-row items-center gap-1.5">
              <BaseIcon size="24">rocket_launch</BaseIcon>
              <h3 class="text-title-medium">
                {{ $t('lumia.sections.mission') }}
              </h3>
            </div>
            <LessonImpulseField class="text-body-large" :value="impulse.mission" />
          </section>
          <div class="mt-10 grid grid-cols-2 gap-4">
            <article class="bg-surface-container rounded-large p-6">
              <h3 class="text-title-large text-on-surface-variant mb-2">
                {{ $t('lumia.sections.prerequisitesStudents') }}
              </h3>
              <LessonImpulseField :value="impulse.studentPrerequisites" class="text-body-large" />
            </article>
            <article class="bg-surface-container rounded-large p-6">
              <h3 class="text-title-large text-on-surface-variant mb-2">
                {{ $t('lumia.sections.lebensweltbezug') }}
              </h3>
              <LessonImpulseField
                class="text-body-large text-on-surface"
                :value="impulse.context.realWorldConnection"
              />
            </article>
          </div>
        </section>
      </TabPanel>
      <TabPanel>
        <section>
          <h2 class="text-headline-medium mb-4">{{ $t('lumia.impulseTabs.preparation') }}</h2>
          <div
            v-html="impulse.teacherPrerequisites"
            class="[&_li]:bg-surface-container [&_li]:rounded-extra-small [&_li:first-child]:rounded-t-large [&_li:last-child]:rounded-b-large [&_li]:p-4 [&>ul]:flex [&>ul]:flex-col [&>ul]:gap-0.5"
          ></div>
        </section>
      </TabPanel>
      <TabPanel>
        <LessonImpulsePhases
          :phases="impulse.phases"
          :materials="impulse.materials"
          :reflexion-questions="impulse.reflexionQuestions"
          :lesson-feedback="lessonFeedback"
          :mission="impulse.mission"
          :indicators="
            impulse.ueberfachlicheZiele.flatMap((kompetenz) =>
              kompetenz.kompetenzstufen.flatMap((stufe) => stufe.indikatoren),
            )
          "
        />
      </TabPanel>
      <TabPanel>
        <section>
          <h2 class="text-headline-medium mb-4">
            {{ $t('lumia.impulseTabs.supportAndLanguage') }}
          </h2>
          <div v-if="hasIndividualSupport" class="grid grid-cols-2 gap-4">
            <article class="bg-surface-container rounded-large p-4">
              <h4 class="text-title-medium mb-2">
                <span
                  class="rounded-small bg-primary-container text-on-primary-container mr-2 inline-flex px-1.5 py-0.5"
                >
                  {{ $t('lumia.sections.support') }}
                </span>
                {{ $t('lumia.sections.moreSupport') }}
              </h4>
              <LessonImpulseField
                class="text-body-large text-on-surface mb-4"
                :value="impulse.context.differentiation.support"
              />
            </article>
            <article class="bg-surface-container rounded-large p-4">
              <h4 class="text-title-medium mb-2">
                <span
                  class="rounded-small bg-tertiary-container text-on-tertiary-container mr-2 inline-flex px-1.5 py-0.5"
                >
                  {{ $t('lumia.sections.enrichment') }}
                </span>
                {{ $t('lumia.sections.lessSupport') }}
              </h4>
              <LessonImpulseField
                class="text-body-large text-on-surface"
                :value="impulse.context.differentiation.enrichment"
              />
            </article>
          </div>
          <article>
            <h3 class="text-title-medium text-on-surface-variant mt-10 mb-2">
              {{ $t('lumia.sections.vocabulary') }}
            </h3>
            <BaseCard type="filled" class="flex flex-col gap-2 pt-2">
              <div class="table border-collapse">
                <div class="table-row">
                  <div class="table-cell pb-2 pl-6">
                    <span class="text-label-medium text-on-surface-variant whitespace-nowrap">
                      {{ $t('lumia.vocabulary.term') }}
                    </span>
                  </div>
                  <div class="table-cell px-6">
                    <span class="text-label-medium text-on-surface-variant whitespace-nowrap">
                      {{ $t('lumia.vocabulary.explanation') }}
                    </span>
                  </div>
                  <div class="table-cell pr-6">
                    <span class="text-label-medium text-on-surface-variant whitespace-nowrap">
                      {{ $t('lumia.vocabulary.example') }}
                    </span>
                  </div>
                </div>
                <div
                  v-for="(entry, index) in impulse.context.languageSupport"
                  :key="index"
                  class="border-outline-variant table-row border-b align-top last:border-b-0"
                >
                  <div class="table-cell py-6 pl-6">
                    <span class="text-title-medium text-primary whitespace-nowrap">
                      {{ entry.term }}
                    </span>
                  </div>
                  <div class="table-cell py-6 pr-6 pl-6">
                    <LessonImpulseField html :value="entry.explanation" />
                  </div>
                  <div class="table-cell py-6 pr-6">
                    <LessonImpulseField
                      html
                      :value="entry.exampleSentence"
                      class="text-on-surface-variant italic"
                    />
                  </div>
                </div>
              </div>
            </BaseCard>
          </article>
        </section>
      </TabPanel>
      <TabPanel>
        <section v-if="!showRunningFeedback">
          <h2 class="text-headline-medium mb-4">
            {{ $t('lumia.impulseTabs.evaluationAndReflection') }}
          </h2>

          <div v-if="impulse.ueberfachlicheZiele.length" class="mt-6 flex flex-col">
            <div
              class="group rounded-large bg-surface-container relative mb-0.5 flex cursor-pointer flex-row items-center gap-2 overflow-hidden p-4"
              :class="[ueberfachlicheZieleExtended && 'rounded-b-small!']"
              @click="ueberfachlicheZieleExtended = !ueberfachlicheZieleExtended"
            >
              <h3 class="text-title-medium text-on-surface-variant grow">
                {{ $t('lumia.sections.transversalGoals') }}
              </h3>
              <div
                class="text-label-medium text-on-surface-variant bg-surface-container-lowest inline-flex h-6 w-6 items-center justify-center rounded-full"
              >
                {{
                  impulse.ueberfachlicheZiele
                    .flatMap((kompetenz) => kompetenz.kompetenzstufen)
                    .flatMap((kompetenzstufe) => kompetenzstufe.indikatoren).length
                }}
              </div>
              <BaseIcon
                class="text-on-surface-variant"
                :class="[ueberfachlicheZieleExtended && 'rotate-180']"
                >keyboard_arrow_down</BaseIcon
              >
              <BaseStateLayer />
            </div>
            <BaseAnimatedExtend :is-extended="ueberfachlicheZieleExtended">
              <div class="flex flex-col gap-0.5">
                <div
                  v-for="(kompetenz, index) in impulse.ueberfachlicheZiele"
                  :key="index"
                  class="rounded-small last:rounded-b-large bg-surface-container flex flex-col gap-3 p-4"
                >
                  <h4 class="flex flex-row items-center gap-2">
                    <div class="flex w-12 shrink-0">
                      <BaseChip :color="colorForUeberfachlicheKompetenz(kompetenz)">
                        {{ kompetenz.code }}
                      </BaseChip>
                    </div>
                    <span class="text-title-medium">{{ kompetenz.title }}</span>
                  </h4>
                  <ul class="flex flex-col gap-3">
                    <li
                      v-for="kompetenzstufe in kompetenz.kompetenzstufen"
                      :key="kompetenzstufe.id"
                    >
                      <div class="flex flex-row items-center gap-2">
                        <div class="flex w-12 shrink-0">
                          <BaseChip
                            :color="colorForUeberfachlicheKompetenz(kompetenz)"
                            class="bg-transparent!"
                          >
                            {{ kompetenzstufe.code }}
                          </BaseChip>
                        </div>
                        <span class="text-body-medium">
                          {{ $t('lumia.competency.studentsCan', { text: kompetenzstufe.text }) }}
                        </span>
                      </div>
                      <ul class="mt-2 flex flex-col gap-0.5 pl-14">
                        <li
                          v-for="indikator in kompetenzstufe.indikatoren"
                          :key="indikator.id"
                          class="bg-surface-container-lowest rounded-extra-small first:rounded-t-large last:rounded-b-large flex flex-row p-3"
                        >
                          <BaseShape
                            shape="arrow"
                            class="mt-1 mr-2 h-3 w-3 shrink-0 rotate-90"
                            :class="'text-' + colorForUeberfachlicheKompetenz(kompetenz) + '-60'"
                          />
                          <span class="text-body-medium">
                            {{
                              $t('lumia.competency.studentCan', {
                                text: indikator.teacherPhrasing,
                              })
                            }}
                          </span>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </div>
            </BaseAnimatedExtend>
          </div>
          <div v-if="impulse.standortbestimmung.length" class="mt-6 flex flex-col">
            <div
              class="group rounded-large bg-surface-container relative mb-0.5 flex cursor-pointer flex-row items-center gap-2 overflow-hidden p-4"
              :class="[standortbestimmungExtended && 'rounded-b-small!']"
              @click="standortbestimmungExtended = !standortbestimmungExtended"
            >
              <h3 class="text-title-medium text-on-surface-variant grow">
                {{ $t('lumia.sections.standortbestimmung') }}
              </h3>
              <div
                class="text-label-medium text-on-surface-variant bg-surface-container-lowest inline-flex h-6 w-6 items-center justify-center rounded-full"
              >
                {{
                  impulse.standortbestimmung.flatMap((kompetenz) => kompetenz.indikatoren).length
                }}
              </div>
              <BaseIcon
                class="text-on-surface-variant"
                :class="[standortbestimmungExtended && 'rotate-180']"
                >keyboard_arrow_down</BaseIcon
              >
              <BaseStateLayer />
            </div>
            <BaseAnimatedExtend :is-extended="standortbestimmungExtended">
              <div class="flex flex-col gap-0.5">
                <div
                  v-for="kompetenz in impulse.standortbestimmung"
                  :key="kompetenz.id"
                  class="rounded-small last:rounded-b-large bg-surface-container flex flex-col gap-3 p-4"
                >
                  <h4 class="flex flex-row items-center gap-2">
                    <div class="flex w-12 shrink-0">
                      <BaseChip :color="colorForUeberfachlicheKompetenz(kompetenz)">
                        {{ kompetenz.code }}
                      </BaseChip>
                    </div>
                    <span class="text-title-medium">{{ kompetenz.title }}</span>
                  </h4>
                  <ul class="flex flex-col gap-0.5 pl-14">
                    <li
                      v-for="indikator in kompetenz.indikatoren"
                      :key="indikator.id"
                      class="bg-surface-container-lowest rounded-extra-small first:rounded-t-large last:rounded-b-large flex flex-row p-3"
                    >
                      <BaseShape
                        shape="arrow"
                        class="mt-1 mr-2 h-3 w-3 shrink-0 rotate-90"
                        :class="'text-' + colorForUeberfachlicheKompetenz(kompetenz) + '-60'"
                      />
                      <span class="text-body-medium">
                        {{
                          $t('lumia.competency.studentCan', {
                            text: indikator.teacherPhrasing,
                          })
                        }}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </BaseAnimatedExtend>
          </div>
          <div v-if="impulse.subjectGoals.length" class="mt-6 flex flex-col">
            <div
              class="group rounded-large bg-surface-container relative mb-0.5 flex cursor-pointer flex-row items-center gap-2 overflow-hidden p-4"
              :class="[subjectGoalsExtended && 'rounded-b-small!']"
              @click="subjectGoalsExtended = !subjectGoalsExtended"
            >
              <h3 class="text-title-medium text-on-surface-variant grow">
                {{ $t('lumia.sections.subjectGoals') }}
              </h3>
              <div
                class="text-label-medium text-on-surface-variant bg-surface-container-lowest inline-flex h-6 w-6 items-center justify-center rounded-full"
              >
                {{
                  impulse.subjectGoals
                    .flatMap((fach) => fach.kompetenzbereiche)
                    .flatMap((kompetenzbereich) => kompetenzbereich.kompetenzen)
                    .flatMap((kompetenz) => kompetenz.kompetenzstufen)
                    .flatMap((kompetenzstufe) => kompetenzstufe.kompetenzteilstufen).length
                }}
              </div>
              <BaseIcon
                class="text-on-surface-variant"
                :class="[subjectGoalsExtended && 'rotate-180']"
                >keyboard_arrow_down</BaseIcon
              >
              <BaseStateLayer />
            </div>
            <BaseAnimatedExtend :is-extended="subjectGoalsExtended">
              <div class="flex flex-col gap-0.5">
                <template v-for="fach in impulse.subjectGoals" :key="fach.code">
                  <div
                    v-for="kompetenzbereich in fach.kompetenzbereiche"
                    :key="kompetenzbereich.code"
                    class="rounded-small last:rounded-b-large bg-surface-container flex flex-col gap-3 p-4"
                  >
                    <h4 class="flex flex-row items-center gap-2">
                      <div class="flex shrink-0">
                        <BaseChip>
                          {{ kompetenzbereich.code }}
                        </BaseChip>
                      </div>
                      <span class="text-title-medium">{{ kompetenzbereich.name }}</span>
                    </h4>
                    <ul class="flex flex-col gap-3">
                      <li v-for="kompetenz in kompetenzbereich.kompetenzen" :key="kompetenz.code">
                        <div class="flex flex-row gap-2">
                          <BaseChip class="bg-transparent!"> {{ kompetenz.code }} </BaseChip>
                          <div class="flex flex-col">
                            <span class="text-body-medium">
                              {{ kompetenz.text }}
                            </span>
                            <ul class="mt-2 flex flex-col gap-0.5">
                              <li
                                v-for="kompetenzteilstufen in kompetenz.kompetenzstufen.flatMap(
                                  (stufe) => stufe.kompetenzteilstufen,
                                )"
                                :key="kompetenzteilstufen.code"
                                class="bg-surface-container-lowest rounded-extra-small first:rounded-t-large last:rounded-b-large flex flex-row p-3"
                              >
                                <BaseShape
                                  shape="arrow"
                                  class="text-primary mt-1 mr-2 h-3 w-3 shrink-0 rotate-90"
                                />
                                <span class="text-body-medium">
                                  {{
                                    $t('lumia.competency.studentsCan', {
                                      text: kompetenzteilstufen.text,
                                    })
                                  }}
                                </span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </template>
              </div>
            </BaseAnimatedExtend>
          </div>
          <div v-if="impulse.reflexionQuestions.length" class="mt-6 flex flex-col">
            <div
              class="group rounded-large bg-surface-container relative mb-0.5 flex cursor-pointer flex-row items-center gap-2 overflow-hidden p-4"
              :class="[reflexionQuestionsExtended && 'rounded-b-small!']"
              @click="reflexionQuestionsExtended = !reflexionQuestionsExtended"
            >
              <h3 class="text-title-medium text-on-surface-variant grow">
                {{ $t('lumia.sections.reflexionQuestions') }}
              </h3>
              <div
                class="text-label-medium text-on-surface-variant bg-surface-container-lowest inline-flex h-6 w-6 items-center justify-center rounded-full"
              >
                {{ impulse.reflexionQuestions.length }}
              </div>
              <BaseIcon
                class="text-on-surface-variant"
                :class="[reflexionQuestionsExtended && 'rotate-180']"
                >keyboard_arrow_down</BaseIcon
              >
              <BaseStateLayer />
            </div>
            <BaseAnimatedExtend :is-extended="reflexionQuestionsExtended">
              <ul class="flex flex-col gap-0.5">
                <li
                  v-for="question in impulse.reflexionQuestions"
                  :key="question.id"
                  class="last:[&>div]:rounded-b-large"
                >
                  <div class="bg-surface-container rounded-extra-small p-4">
                    {{ question.text }}
                  </div>
                </li>
              </ul>
            </BaseAnimatedExtend>
          </div>
        </section>
        <SelfAssessmentPage v-if="showRunningFeedback" />
      </TabPanel>
    </TabPanels>

    <section v-if="leftoverMaterials.length" class="flex flex-col gap-3">
      <h2 class="text-title-large">{{ $t('lumia.sections.furtherMaterials') }}</h2>
      <LessonImpulseMaterialCard
        v-for="material in leftoverMaterials"
        :key="material.materialId"
        :material="material"
      />
    </section>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import { TabPanel, TabPanels } from '@headlessui/vue'
  import BaseCard from '@/core/components/base/card/BaseCard.vue'
  import BaseChip from '@/core/components/base/chip/BaseChip.vue'
  import BaseShape from '@/core/components/base/shapes/BaseShape.vue'
  import { colorForUeberfachlicheKompetenz } from '../models/ueberfachliche-kompetenz-color'
  import type { Member } from '@/core/models/member/member'
  import type { LessonTeam } from '../models/lesson'
  import { unassignedMaterials, type LessonImpulse } from '../models/lesson-impulse'
  import LessonImpulseField from './LessonImpulseField.vue'
  import LessonImpulseMaterialCard from './LessonImpulseMaterialCard.vue'
  import LessonImpulsePhases from './LessonImpulsePhases.vue'
  import SelfAssessmentPage from '../pages/SelfAssessmentPage.vue'
  import BaseAnimatedExtend from '@/core/components/base/animated-extend/BaseAnimatedExtend.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseStateLayer from '@/core/components/base/state-layer/BaseStateLayer.vue'

  interface Props {
    impulse: LessonImpulse
    showRunningFeedback?: boolean
    /** Set on a running Lesson. Reflexion phases offer the link or the start action from it. */
    lessonFeedback?: {
      readonly running: boolean
      readonly teams: ReadonlyArray<LessonTeam>
      readonly members: ReadonlyArray<Member>
    }
  }

  const props = defineProps<Props>()

  const leftoverMaterials = computed(() =>
    unassignedMaterials(props.impulse.materials, props.impulse.phases),
  )

  const hasIndividualSupport = computed(() => {
    const differentiation = props.impulse.context.differentiation
    return Boolean(differentiation?.support || differentiation?.enrichment)
  })

  const subjectGoalsExtended = ref(false)
  const ueberfachlicheZieleExtended = ref(true)
  const reflexionQuestionsExtended = ref(true)
  const standortbestimmungExtended = ref(true)
</script>
